import { motion } from "framer-motion";
import { Play, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { HERO_POSTER, STATS } from "@/lib/siteData";

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative isolate min-h-[100svh] w-full overflow-hidden vignette grain"
    >
      {/* Background image with Ken Burns slow zoom */}
      <div className="absolute inset-0 -z-10">
        <img
          data-testid="hero-background-image"
          src={HERO_POSTER}
          alt="Cinematic Cape Town video production still by Pie in the Sky Productions"
          className="h-full w-full object-cover kenburns"
          loading="eager"
          decoding="async"
        />
      </div>

      <div className="container-site relative flex min-h-[100svh] flex-col justify-end pb-16 pt-28 sm:pt-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-6xl"
        >
          <p className="kicker text-white/70 mb-4" data-testid="hero-kicker">Cape Town video production</p>
          <h2 className="font-display text-white uppercase text-[clamp(3.5rem,10vw,10rem)] leading-[0.85] tracking-[-0.01em]" data-testid="hero-title">
            <span className="text-[hsl(var(--primary))]">Narratives</span><br />
            that captivate<br />&amp; convert.
          </h2>
          <p className="mt-6 text-white/70 text-base sm:text-lg max-w-2xl" data-testid="hero-subtitle">
            We&apos;re an award-winning crew of filmmakers, cinematographers and storytellers - producing brand films, commercials, documentaries and corporate video out of Cape Town.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button
              data-testid="hero-primary-cta-button"
              onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
              className="h-12 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-110 px-6 text-sm font-medium"
            >
              Start a project <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              asChild
              data-testid="hero-showreel-button"
              variant="ghost"
              className="h-12 rounded-full border border-white/30 bg-white/5 text-white hover:bg-white/10 hover:border-white/50 px-5 text-sm font-medium backdrop-blur-sm"
            >
              <Link to="/portfolio" aria-label="Browse our video portfolio">
                <Play className="mr-2 h-4 w-4 fill-current" /> Watch showreel
              </Link>
            </Button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 0.4 }}
          className="mt-14 sm:mt-20 border-t border-white/10 pt-6 grid grid-cols-2 sm:grid-cols-4 gap-y-4"
        >
          {STATS.map((s) => (
            <div key={s.label} data-testid={`hero-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="font-display text-3xl sm:text-4xl text-white">{s.value}</div>
              <div className="text-xs uppercase tracking-[0.18em] text-white/55 mt-1">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Scroll indicator (desktop only, right side) */}
      <div aria-hidden className="pointer-events-none absolute bottom-8 right-6 hidden lg:flex text-white/55 rotate-90 origin-bottom-right">
        <div className="flex items-center gap-3">
          <span className="block h-px w-8 bg-white/40" />
          <span className="font-mono-credits text-[10px] uppercase tracking-[0.32em]">Scroll</span>
        </div>
      </div>
    </section>
  );
};
